import React, { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { useLocation } from "react-router-dom";

type AppErrorBoundaryProps = React.PropsWithChildren<{
  title?: string;
  description?: string;
  resetKey?: string;
}>;

type AppErrorBoundaryState = {
  hasError: boolean;
  message?: string;
};

function getErrorMessage(error: unknown) {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === "string" && error.trim()) return error;
  return "Unexpected application error.";
}

export class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return {
      hasError: true,
      message: getErrorMessage(error),
    };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    console.error("[Xentros render error]", error, info);
  }

  componentDidUpdate(prevProps: AppErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, message: undefined });
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="xErrorBoundary" role="alert">
        <div className="xErrorBoundaryIcon" aria-hidden="true">
          <AlertTriangle size={22} strokeWidth={1.9} />
        </div>
        <div className="xErrorBoundaryBody">
          <h1>{this.props.title ?? "Something went wrong"}</h1>
          <p>
            {this.props.description ??
              "This view could not be displayed correctly. The rest of Xentros is still available."}
          </p>
          <div className="xErrorBoundaryMessage">{this.state.message}</div>
          <div className="xErrorBoundaryActions">
            <button type="button" className="xErrorBoundaryButton xErrorBoundaryButton--primary" onClick={this.handleReload}>
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export function RouteErrorBoundary({ children }: React.PropsWithChildren) {
  const location = useLocation();

  return (
    <AppErrorBoundary
      resetKey={`${location.pathname}${location.search}`}
      title="This screen could not be displayed"
      description="This view could not be displayed correctly. Please reload the app."
    >
      {children}
    </AppErrorBoundary>
  );
}

type RuntimeErrorNotice = {
  id: number;
  message: string;
};

function getPromiseRejectionMessage(reason: unknown) {
  return getErrorMessage(reason);
}

export function GlobalRuntimeErrorBanner() {
  const [notice, setNotice] = useState<RuntimeErrorNotice | null>(null);

  useEffect(() => {
    const showNotice = (message: string) => {
      setNotice({
        id: Date.now(),
        message,
      });
    };

    const handleError = (event: ErrorEvent) => {
      showNotice(event.message || getErrorMessage(event.error));
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      showNotice(getPromiseRejectionMessage(event.reason));
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  if (!notice) return null;

  return (
    <div className="xRuntimeErrorBanner" role="alert">
      <AlertTriangle size={18} strokeWidth={1.9} aria-hidden="true" />
      <div className="xRuntimeErrorBannerText">
        <strong>Background error</strong>
        <span>{notice.message}</span>
      </div>
      <button type="button" className="xRuntimeErrorDismiss" onClick={() => setNotice(null)} aria-label="Dismiss error">
        <X size={16} strokeWidth={1.9} />
      </button>
    </div>
  );
}
